import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ExternalLink } from 'lucide-react'
import { api } from '@personic/shared'

interface Profile {
  id: string
  canonical_id: string
  personality_signals: Record<string, number>
  preference_signals: Record<string, unknown>
  distillation_count: number
  created_at: string
  updated_at: string
}

export default function Profiles() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null)
  const [activeTab, setActiveTab] = useState<'personality' | 'preferences' | 'persona'>('personality')

  const { data: profiles, isLoading } = useQuery<Profile[]>({
    queryKey: ['profiles', searchQuery],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (searchQuery) params.set('q', searchQuery)
      params.set('limit', '50')
      const res = await api.get(`/v3/profiles?${params}`)
      return res.data
    },
  })

  const { data: persona, isLoading: personaLoading } = useQuery({
    queryKey: ['persona', selectedProfile?.id],
    queryFn: async () => {
      if (!selectedProfile) return null
      const res = await api.post(`/v3/profile/${selectedProfile.id}/persona`, {
        product_context: 'engage',
      })
      return res.data
    },
    enabled: !!selectedProfile && activeTab === 'persona',
  })

  if (isLoading) {
    return <div className="text-gray-500">Loading...</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Profiles</h2>
        <p className="text-gray-600 mt-1">Browse and manage personality profiles for your users</p>
      </div>

      {/* Search */}
      <div className="card p-4">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by canonical ID..."
          className="input w-full"
        />
      </div>

      {/* Main Content */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Profile List */}
        <div className="lg:col-span-1">
          <div className="card overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <span className="text-sm font-medium text-gray-700">
                {profiles?.length || 0} Profiles
              </span>
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {profiles?.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  No profiles found
                </div>
              ) : (
                profiles?.map((profile) => (
                  <button
                    key={profile.id}
                    onClick={() => setSelectedProfile(profile)}
                    className={`w-full px-4 py-3 text-left border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                      selectedProfile?.id === profile.id ? 'bg-personic-50 border-l-2 border-l-personic-600' : ''
                    }`}
                  >
                    <div className="font-medium text-gray-900 truncate">
                      {profile.canonical_id}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span>{profile.distillation_count} distillations</span>
                      <span>•</span>
                      <span>{new Date(profile.updated_at).toLocaleDateString()}</span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Profile Detail */}
        <div className="lg:col-span-2">
          {selectedProfile ? (
            <div className="card">
              {/* Profile Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {selectedProfile.canonical_id}
                    </h3>
                    <p className="text-sm text-gray-500 font-mono mt-1">{selectedProfile.id}</p>
                  </div>
                  <Link
                    to={`/tenant/profiles/${selectedProfile.id}`}
                    className="flex items-center gap-1.5 text-sm text-copper-600 hover:text-copper-700 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Full View
                  </Link>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200">
                <nav className="flex -mb-px">
                  {(['personality', 'preferences', 'persona'] as const).map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-6 py-3 text-sm font-medium border-b-2 ${
                        activeTab === tab
                          ? 'border-personic-600 text-personic-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'personality' && (
                  <PersonalityView signals={selectedProfile.personality_signals} />
                )}
                {activeTab === 'preferences' && (
                  <PreferencesView signals={selectedProfile.preference_signals} />
                )}
                {activeTab === 'persona' && (
                  <PersonaView persona={persona} isLoading={personaLoading} />
                )}
              </div>
            </div>
          ) : (
            <div className="card p-12 text-center text-gray-500">
              <div className="text-4xl mb-4">👤</div>
              <p>Select a profile to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function PersonalityView({ signals }: { signals: Record<string, number> }) {
  const categories = {
    'Communication Style': ['verbosity', 'formality', 'directness', 'technical_depth'],
    'Cognitive Preferences': ['detail_preference', 'pace', 'structure_preference'],
    'Big Five': ['openness', 'conscientiousness', 'extraversion', 'agreeableness', 'neuroticism'],
  }

  return (
    <div className="space-y-6">
      {Object.entries(categories).map(([category, traits]) => (
        <div key={category}>
          <h4 className="text-sm font-medium text-gray-700 mb-3">{category}</h4>
          <div className="space-y-3">
            {traits.map((trait) => {
              const value = signals[trait] ?? 0.5
              return (
                <div key={trait}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600 capitalize">
                      {trait.replace(/_/g, ' ')}
                    </span>
                    <span className="font-medium text-gray-900">
                      {(value * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-personic-600 rounded-full transition-all"
                      style={{ width: `${value * 100}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

function PreferencesView({ signals }: { signals: Record<string, unknown> }) {
  if (!signals || Object.keys(signals).length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        No preference data available yet
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {Object.entries(signals).map(([domain, prefs]) => (
        <div key={domain} className="border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 capitalize mb-2">{domain}</h4>
          <pre className="text-sm text-gray-600 bg-gray-50 p-3 rounded overflow-x-auto">
            {JSON.stringify(prefs, null, 2)}
          </pre>
        </div>
      ))}
    </div>
  )
}

function PersonaView({
  persona,
  isLoading,
}: {
  persona: { persona_document: string } | null
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <div className="text-center text-gray-500 py-8">
        Generating persona...
      </div>
    )
  }

  if (!persona) {
    return (
      <div className="text-center text-gray-500 py-8">
        Click on the Persona tab to generate an AI-optimized persona document
      </div>
    )
  }

  return (
    <div className="prose prose-sm max-w-none">
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-2">AI Persona Document</h4>
        <p className="text-gray-600 whitespace-pre-wrap">{persona.persona_document}</p>
      </div>
      <p className="text-xs text-gray-500 mt-4">
        This document is optimized for injection into LLM system prompts to personalize responses.
      </p>
    </div>
  )
}
